import { useDisclosure } from '@chakra-ui/hooks';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  CmBox,
  CmButton,
  CmModal,
  CmModalBody,
  CmModalFooter,
  CmText,
} from '@/components/uiParts';
import { routes } from '@/router';
import {
  CreateIntegrationReadyDeliveryStoreParams,
  useCreateIntegrationReadyDeliveryStore,
} from '@/services/StoreManagement/IntegrationReadyDeliveryStore/hooks/useCreateIntegrationReadyDeliveryStore';
import { UberStoreListResponse } from '@/services/StoreManagement/UberStores/hooks';

interface Props {
  selectedUberStoreIDs: string[];
  uberStores: UberStoreListResponse[];
  isDisabledAuthButton: boolean;
  isOrderManager: boolean;
}

// 未連携（linked_status === null）の Uber Eats 店舗を Camel に新規連携するモーダル。
// 連携済店舗の is_order_manager 切替は OrderManagerToggleAction で行うため、本モーダルは扱わない。
export const CreationConfirmModal: React.VFC<Props> = ({
  selectedUberStoreIDs,
  uberStores,
  isDisabledAuthButton,
  isOrderManager,
}) => {
  const history = useHistory();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [
    createIntegrationReadyDeliveryStoreParams,
    setCreateIntegrationReadyDeliveryStoreParams,
  ] = useState<CreateIntegrationReadyDeliveryStoreParams[]>([]);

  const handleCreateIntegrationReadyDeliveryStoreParams = () => {
    onOpen();
    const params: CreateIntegrationReadyDeliveryStoreParams[] = [];
    selectedUberStoreIDs.forEach((uberStoreID) => {
      uberStores.forEach((uberStore) => {
        if (uberStoreID === uberStore.id) {
          params.push({
            external_store_id: uberStore.id,
            external_store_name: uberStore.name,
            delivery_service_id: 1,
            is_order_manager: isOrderManager,
          });
        }
      });
    });
    setCreateIntegrationReadyDeliveryStoreParams(params);
  };

  const handleCreateIntegrationReadyDeliveryStores = async (
    params: CreateIntegrationReadyDeliveryStoreParams[]
  ) => {
    await useCreateIntegrationReadyDeliveryStore(params);
    alert('認証が完了しました。店舗一覧ページに戻ります。');
    history.push(routes.storeManagementMasterStores.path);
  };

  const buttonLabel = isOrderManager
    ? '認証（標準）'
    : '認証（メニュー連携のみ）';

  const modalTitle = isOrderManager
    ? '以下のUber Eats店舗を標準で新規連携しますか？'
    : '以下のUber Eats店舗をメニュー連携のみで新規連携しますか？';

  return (
    <>
      {/*
       // @ts-ignore TS2590 */}
      <CmButton
        width={isOrderManager ? 120 : 200}
        mb={2}
        ml={2}
        onClick={handleCreateIntegrationReadyDeliveryStoreParams}
        isDisabled={isDisabledAuthButton}
        colorScheme={isOrderManager ? 'teal' : 'gray'}
      >
        {buttonLabel}
      </CmButton>

      <CmModal
        title={modalTitle}
        headerSize="md"
        size="4xl"
        {...{ isOpen, onClose }}
      >
        <CmModalBody textAlign="center">
          {createIntegrationReadyDeliveryStoreParams.map((uberStore, index) => {
            return (
              <CmText key={index} fontSize="2xl">
                {uberStore.external_store_name}
              </CmText>
            );
          })}
          {!isOrderManager && (
            <CmBox mt={4} p={3} bg="orange.50" borderRadius="md">
              <CmText fontSize="sm" color="orange.800">
                Camelと注文連携する際は必ず「認証（標準）」の設定が必要になります。
                <br />
                引き続きUberEatsアプリで注文を受け付ける場合のみ、「認証（メニュー連携のみ）」をご利用ください。
              </CmText>
            </CmBox>
          )}
        </CmModalBody>
        <CmModalFooter>
          <CmButton variant="outline" mr={4} onClick={onClose}>
            キャンセル
          </CmButton>
          <CmButton
            width={100}
            colorScheme="teal"
            onClick={() =>
              handleCreateIntegrationReadyDeliveryStores(
                createIntegrationReadyDeliveryStoreParams
              )
            }
          >
            確定
          </CmButton>
        </CmModalFooter>
      </CmModal>
    </>
  );
};
